import {
  AfterViewInit,
  ChangeDetectorRef,
  Directive,
  EventEmitter,
  inject,
  OnDestroy,
  OnInit,
  Output,
} from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { isEqual } from 'lodash';
import { combineLatest, Subscription } from 'rxjs';
import { SessionService } from 'src/app/auth/session.service';
import { safelyDetectChanges, unsubscribeAll } from 'src/app/common/app.helpers';
import AppSettings from 'src/app/common/app.settings';
import AppValues from 'src/app/common/app.values';
import { FormValueService } from 'src/app/common/guards/save-data-guard/form-value.service';
import { createLink } from 'src/app/components/profile/user-profile/view-profile/regular-user-profile/regular-user-profile-helper';
import { FormGroupControls } from 'src/app/interfaces/form-and-field.interface';
import { Session } from 'src/app/interfaces/session.interface';
import { EntityStatus, ItemStatus } from 'src/app/interfaces/views.interface';
import { ValidationService } from 'src/app/services/validation.service';
import { SendMessageFormService } from './send-message-form.service';

export interface MessageForm {
  messageInput: string;
}

@Directive()
export abstract class ConversationForm implements OnInit, AfterViewInit, OnDestroy {
  public readonly DEFAULT_PROFILE_IMAGE: string = AppSettings.DEFAULT_USER_IMAGE;

  @Output() public dirtyForm: EventEmitter<boolean> = new EventEmitter<boolean>();

  public messageInput: FormControl<string>;
  public messageForm: FormGroupControls<MessageForm>;
  public messageSubmitted = false;

  public isAdminSession = false;
  public currentUserImage = '';
  public currentUserAccountStatus: EntityStatus = ItemStatus.EMPTY;

  private subscriptions$: Subscription[] = [];

  private sessionService = inject(SessionService);
  private formValueService = inject(FormValueService);
  private sendMessageFormService = inject(SendMessageFormService);
  private validationService = inject(ValidationService);
  protected changeDetectorRef = inject(ChangeDetectorRef);

  public ngOnInit(): void {
    this.subscribeToSessionChanges();
    this.createForm();
  }

  public ngAfterViewInit(): void {
    setTimeout(() => {
      this.formValueService.saveFormValue({ messageInput: '' });
      this.dirtyForm.emit(false);
      this.subscribeToControlValueChanges();
    });
  }

  public ngOnDestroy(): void {
    unsubscribeAll(this.subscriptions$);
  }

  public getLink(): string {
    if (this.sessionService.user) {
      return createLink(this.sessionService.user.account_status, this.sessionService.user.user_id);
    } else return '';
  }

  public abstract submitMessage(): void;
  public abstract getValidationError(): string;

  public onKeyDownEvent(event: KeyboardEvent): void {
    const isModifiersKey = event.shiftKey || event.altKey || event.ctrlKey;

    if (event.code === AppValues.KEYCODE_ENTER && isModifiersKey) {
      event.preventDefault();
      this.messageInput.setValue(this.messageInput.value + '\n');
    }

    if (event.code === AppValues.KEYCODE_ENTER && !isModifiersKey) {
      event.preventDefault();
      this.submitMessage();
    }
  }

  /**
   * @description Subscribes to sessionChanges event and calls setUserImage() method on it.
   */
  protected subscribeToSessionChanges(): void {
    this.subscriptions$.push(
      this.sessionService.sessionChanges.subscribe((session: Session) => {
        this.isAdminSession = session.adminSession;
        this.setUserImage();
        this.setUserAccountStatus();
      }),
    );

    this.setUserImage();
    this.setUserAccountStatus();
  }

  /**
   * @description Updates currentUserImage property with user avatar from session.
   */
  protected setUserImage(): void {
    this.currentUserImage = this.sessionService.user ? this.sessionService.user.image_url : '';
  }

  /**
   * @description Updates currentUserAccountStatus property with user Account Status from session.
   */
  protected setUserAccountStatus(): void {
    this.currentUserAccountStatus = this.sessionService.user
      ? this.sessionService.user.account_status
      : ItemStatus.EMPTY;
  }

  protected abstract getMaxLengthConstraint(): number;

  protected createForm(): void {
    this.messageInput = new FormControl('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.maxLength(this.getMaxLengthConstraint()),
        ...this.validationService.getValidators('noWhiteSpacesOnly'),
      ],
    });

    this.messageForm = new FormGroup({
      messageInput: this.messageInput,
    });
  }

  protected subscribeToControlValueChanges(): void {
    this.subscriptions$.push(
      combineLatest([this.messageForm.valueChanges, this.formValueService.valueChanged]).subscribe(
        ([value, startValue]: [object, object]) => {
          if (value) {
            this.dirtyForm.emit(!isEqual(value, startValue));
            this.sendMessageFormService.updateValue(!isEqual(value, startValue));
          }
          safelyDetectChanges(this.changeDetectorRef);
        },
      ),
    );
  }
}
